import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Table } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { getAuthToken } from '../../../util/auth';
import { json, useNavigate } from 'react-router-dom';
import { getApiChara } from '../../../util/getApi';
import CommonDashboard from '../../CommomDashboard';
import Loader from '../../UI/Loader';
import { FaUserPlus, FaSearch, FaSortAmountDown, FaCalendarAlt, FaIdBadge, FaUser, FaPhoneAlt, FaInfoCircle, FaEye, FaMapMarkerAlt, FaSyncAlt } from 'react-icons/fa';
import classes from './PatientList.module.css';
import { useTranslation } from "react-i18next";

const PAGE_SIZE = 10; // Number of items per page

const Patients = () => {
  const { t } = useTranslation();
  const [currentPage, setCurrentPage] = useState(0);
  const [patientLists, setPatientLists] = useState([]);
  const [searchPara, setSearchPara] = useState('');
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [searchName, setSearchName] = useState('');
  const [filteredTableByName, setFilteredTableByName] = useState([]);
  const loaderRef = useRef(null);
  const [sortOrder, setSortOrder] = useState('asc');
  const [isLoading, setIsLoading] = useState(false);
  const [isSearchActive, setIsSearchActive] = useState(false);
  useEffect(() => {
    const fetchData = async () => {
      const token = getAuthToken();
      // Fetch data from the API based on the current page and PAGE_SIZE
      if (token === null || token === 'EXPIRED') {
        navigate('/');
      }

      const apiChar = getApiChara();
      const apiUrl = `${apiChar}/patient/getAllPatient?page=${currentPage}&size=${PAGE_SIZE}`;
      setIsLoading(true);
      try {
        const response = await fetch(apiUrl, {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        });

        if (!response.ok) {
          throw json({ message: 'could not fetch patients' }, { status: 500 });
        }

        const data = await response.json();
        if (data.length < PAGE_SIZE) {
          setHasMore(false); // No more data to load
        }
        setPatientLists((prevData) => [...prevData, ...data]); // Append new data to the existing patient list
        setLoading(false);
      } catch (error) {
        console.error('Error fetching patients:', error);
        setLoading(false);
      }
      finally {
        setIsLoading(false); // Hide loader after fetching data
      }
    };

    fetchData();
  }, [currentPage]);

  const handleObserver = (entities) => {
    const target = entities[0];
    if (target.isIntersecting && hasMore && !loading) {
      setCurrentPage((prevPage) => prevPage + 1); // Load next page when the loader element is in view
    }
  };

  useEffect(() => {
    const observer = new IntersectionObserver(handleObserver, {
      root: null,
      rootMargin: '20px',
      threshold: 0.1,
    });

    if (loaderRef.current) {
      observer.observe(loaderRef.current);
    }

    return () => {
      if (loaderRef.current) {
        observer.unobserve(loaderRef.current);
      }
    };
  }, [loaderRef, hasMore, loading]);

  const OnSearchNameHandler = (sname) => {
    setSearchName(sname.toLowerCase());
  };

  useEffect(() => {
    let filteredPatientByName = patientLists;

    if (searchName && searchName.trim() !== "") {
      filteredPatientByName = patientLists.filter((patient) => {
        const fullName = `${patient?.firstName} ${patient?.middleName} ${patient?.lastName}`;
        return fullName.toLowerCase().includes(searchName.toLowerCase());
      });
    }

    setFilteredTableByName(filteredPatientByName);
  }, [patientLists, searchName]);

  const toggleSortOrder = () => {
    setSortOrder((prevOrder) => (prevOrder === 'asc' ? 'desc' : 'asc'));
  };

  const sortedTable = useMemo(() => {
    return [...filteredTableByName].sort((a, b) => {
      return sortOrder === 'asc' ? a.patientId - b.patientId : b.patientId - a.patientId;
    });
  }, [filteredTableByName, sortOrder]);

  const navigate = useNavigate()
  const currentDate = new Date();
  const formattedDate = `${currentDate.getDate()}-${currentDate.getMonth() + 1
    }-${currentDate.getFullYear()}`;

  const newPatientHandler = () => {
    navigate('new');
  }

  const getSerachParaHandler = (e) => {
    setSearchPara(e.target.value);
  };
  const onSearchPatientHandler = async (e) => {
    e.preventDefault();

    const token = getAuthToken();
    if (token === null || token === 'EXPIRED') {
      navigate('/');
      return;
    }

    setIsLoading(true);
    const apiChar = getApiChara();
    const apiUrl = `${apiChar}/patient/getPatientByFNameOrLNameOrMobile?value=` + searchPara;

    try {
      const response = await fetch(apiUrl, {
        headers: {
          Authorization: "Bearer " + token,
        },
      });

      if (!response.ok) {
        throw new Error("Could Not fetch details");
      }

      const data = await response.json();
      setPatientLists(data);
      setHasMore(false); // Disable infinite scroll during search results
      setIsSearchActive(true);
    } catch (error) {
      console.error("Error fetching patient data: ", error);
    }
    finally {
      setIsLoading(false);
    }
  };

  const onResetSearchHandler = () => {
    window.location.reload();
  };
  return (

    <>
      {isLoading && <Loader />}
      <div className="container">
        <div className="d-flex justify-content-between align-items-center mb-4 mt-2">
          <h4 style={{ color: 'var(--primary-color)', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1px', margin: 0 }}>
            {t("Patient Information")}
          </h4>
          <span className="badge bg-light text-dark border p-2" style={{ fontSize: '0.85rem' }}>
            <FaCalendarAlt className="me-1" style={{ color: 'var(--primary-color)' }} /> {formattedDate}
          </span>
        </div>

        <div className="row g-3 align-items-center mb-4">
          <div className="col-md-6 col-sm-12">
            <form onSubmit={onSearchPatientHandler}>
              <div className="input-group">
                <span className="input-group-text bg-white border-end-0">
                  <FaSearch className="text-muted" />
                </span>
                <input
                  type="text"
                  className="form-control border-start-0 ps-0"
                  placeholder={t("Search by Name or Mobile...")}
                  onChange={getSerachParaHandler}
                  required
                  style={{ boxShadow: 'none' }}
                />
                <button
                  className={`${classes.premiumBtn} border-0`}
                  type="submit"
                  style={{ borderTopLeftRadius: '0px', borderBottomLeftRadius: '0px' }}
                >
                  <FaSearch className="me-2" /> {t("Search")}
                </button>
                {isSearchActive && (
                  <button
                    className={`${classes.premiumBtn} border-0 ms-2`}
                    type="button"
                    onClick={onResetSearchHandler}
                    style={{ borderRadius: '8px' }}
                    title={t("All Patients")}
                  >
                    <FaSyncAlt />
                  </button>
                )}
              </div>
            </form>
          </div>
          <div className="col-md-6 col-sm-12 d-flex justify-content-md-end gap-2">
            <button
              className={classes.premiumBtn}
              onClick={newPatientHandler}
            >
              <FaUserPlus className="me-2" /> {t("New Patient")}
            </button>
          </div>
        </div>
      </div>

      <div className={classes.tableContainer}>
        <Table responsive hover className={classes.customTable}>
          <thead>
            <tr>
              <th onClick={toggleSortOrder} style={{ cursor: 'pointer', width: '150px' }}>
                {t("Patient Id")} <FaSortAmountDown className="ms-2 opacity-50" />
              </th>
              <th>{t("Reg No")}</th>
              <th className="text-start ps-4">
                <FaUser className="me-2" /> {t("Patient Name")}
              </th>
              <th>
                <FaPhoneAlt className="me-2" /> {t("Mobile")}
              </th>
              <th>
                <FaMapMarkerAlt className="me-2" /> {t("Address")}
              </th>
              <th style={{ width: '120px' }}>{t("Action")}</th>
            </tr>
          </thead>
          <tbody>
            {sortedTable.length === 0 && (
              <tr>
                <td colSpan="5" className="py-5 text-center text-muted">
                  <FaInfoCircle className="fa-2x mb-2 d-block opacity-50 mx-auto" />
                  {t("No patient records found.")}
                </td>
              </tr>
            )}
            {sortedTable.map((patient, index) => (
              <tr key={index}>
                <td>
                  <span className="badge rounded-pill bg-light text-dark border">{patient?.patientId}</span>
                </td>
                <td>{patient?.registrationNo || "-"}</td>
                <td className="text-start ps-4 fw-500">
                  {`${patient?.firstName || ""} ${patient?.middleName ? patient.middleName + " " : ""}${patient?.lastName || ""}`}
                </td>
                <td>{patient.mobile || "-"}</td>
                <td>{patient.address || "-"}</td>
                <td>
                  <Link
                    to={`${patient?.patientId}`}
                    className="btn-outline-premium btn btn-sm px-3"
                    title={t("View Details")}
                  >
                    <FaEye />
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      <div className="d-flex justify-content-center mt-4" ref={loaderRef}>
        {loading && <div>{t("Loading...")}</div>}
      </div>
    </>
  );
};

export default Patients;